import type { DataGridProps } from '@cognite/cogs-lab-v11';
import type {
  DirectRelationReference,
  FilterDefinition,
  UserProfileItem,
  ViewReference,
} from '@cognite/sdk';

import {
  COGNITE_SOURCEABLE_CREATED_USER,
  COGNITE_SOURCEABLE_UPDATED_USER,
  DIRECT_RELATION_FILTER_VALUE_SEPARATOR,
} from './consts';
import type {
  ActiveFilters,
  ObservationPropertyFilter,
} from './observation-filter-types';
import type { ObservationViewProperty } from './types';
import { isDirectRelation, isEnumProperty, isFilesProperty } from './utils';

export type BuildActiveFiltersFromFilterModelParams = {
  filterModel: DataGridProps['filterModel'];
  displayableProperties: ObservationViewProperty[];
  sourceableUserPropertyIds: ReadonlySet<string>;
};

const isNonEmptyStringArray = (
  value: unknown
): value is [string, ...string[]] => {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((entry) => typeof entry === 'string')
  );
};

export const getViewPropertyPath = (
  viewReference: ViewReference,
  propertyId: string
): string[] => [
  viewReference.space,
  `${viewReference.externalId}/${viewReference.version}`,
  propertyId,
];

export const parseDirectRelationFilterValue = (
  encoded: string
): DirectRelationReference => {
  const [space, ...externalIdParts] = encoded.split(
    DIRECT_RELATION_FILTER_VALUE_SEPARATOR
  );
  return {
    space: space ?? '',
    externalId: externalIdParts.join(DIRECT_RELATION_FILTER_VALUE_SEPARATOR),
  };
};

export const buildActiveFiltersFromFilterModel = ({
  filterModel,
  displayableProperties,
  sourceableUserPropertyIds,
}: BuildActiveFiltersFromFilterModelParams): ActiveFilters => {
  const items = filterModel?.items ?? [];
  const activeFilters: ActiveFilters = {};

  items.forEach((item) => {
    if (!isNonEmptyStringArray(item.value)) {
      return;
    }

    const values = item.value;

    if (sourceableUserPropertyIds.has(item.field)) {
      activeFilters[item.field] = {
        type: 'enum',
        propertyId: item.field,
        values,
      };
      return;
    }

    const property = displayableProperties.find((p) => p.id === item.field);
    if (!property) {
      return;
    }

    if (isEnumProperty(property)) {
      activeFilters[item.field] = {
        type: 'enum',
        propertyId: item.field,
        values,
      };
      return;
    }

    if (property.type === 'boolean') {
      activeFilters[item.field] = {
        type: 'boolean',
        propertyId: item.field,
        value: values[0] === 'true',
      };
      return;
    }

    if (isDirectRelation(property) && !isFilesProperty(property)) {
      activeFilters[item.field] = {
        type: 'direct',
        propertyId: item.field,
        isList: property.isList,
        values: values.map(parseDirectRelationFilterValue),
      };
    }
  });

  return activeFilters;
};

export const buildObservationFilterFromFilterModel = (
  params: BuildActiveFiltersFromFilterModelParams,
  viewReference: ViewReference
): FilterDefinition | undefined =>
  buildObservationFilter(
    buildActiveFiltersFromFilterModel(params),
    viewReference
  );

/**
 * Converts active filter state into a single DM filter expression to be
 * passed to the instances.search `filter` parameter.
 *
 * Returns undefined when no filters are active so callers can omit the
 * parameter entirely.
 */
export const buildObservationFilter = (
  activeFilters: ActiveFilters,
  viewReference: ViewReference
): FilterDefinition | undefined => {
  const conditions: FilterDefinition[] = Object.values(activeFilters).reduce<
    FilterDefinition[]
  >((acc: FilterDefinition[], filter: ObservationPropertyFilter) => {
    const propertyPath = getViewPropertyPath(viewReference, filter.propertyId);

    switch (filter.type) {
      case 'enum':
        if (filter.values.length === 0) {
          return acc;
        }
        acc.push({ in: { property: propertyPath, values: filter.values } });
        break;
      case 'boolean':
        acc.push({ equals: { property: propertyPath, value: filter.value } });
        break;
      case 'direct':
        if (filter.values.length === 0) {
          return acc;
        }
        if (filter.isList) {
          acc.push({
            containsAny: { property: propertyPath, values: filter.values },
          });
          break;
        }
        acc.push({ in: { property: propertyPath, values: filter.values } });
        break;
    }

    return acc;
  }, []);

  if (conditions.length === 0) {
    return undefined;
  }
  if (conditions.length === 1) {
    return conditions[0];
  }
  return { and: conditions };
};

const isSourceableUserProperty = (
  viewProperty: ObservationViewProperty
): boolean =>
  viewProperty.id === COGNITE_SOURCEABLE_CREATED_USER ||
  viewProperty.id === COGNITE_SOURCEABLE_UPDATED_USER;

export const getSourceableUserPropertyIds = (
  viewProperties: ObservationViewProperty[]
): Set<string> =>
  new Set(
    viewProperties
      .filter(isSourceableUserProperty)
      .map((property) => property.id)
  );

export const isSourceableUserPropertyId = (
  propertyId: string,
  sourceableUserPropertyIds: Set<string>
): boolean => sourceableUserPropertyIds.has(propertyId);

/** Matches UserCell: email, then display name, then raw identifier. */
export const getUserFilterOptionLabel = (
  userIdentifier: string,
  profile?: UserProfileItem
): string => profile?.email || profile?.displayName || userIdentifier;

export const isFilterableObservationProperty = (
  viewProperty: ObservationViewProperty
): boolean => {
  if (isEnumProperty(viewProperty) || viewProperty.type === 'boolean') {
    return true;
  }
  if (isSourceableUserProperty(viewProperty)) {
    return true;
  }
  return isDirectRelation(viewProperty) && !isFilesProperty(viewProperty);
};
